import { SmilePlus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { IS_MOBILE } from "@/lib/environmentDetector";
import { cn } from "@/lib/utils";
import { useState, useRef, useEffect, useMemo } from "react";
import EmojiPicker, {
  Theme,
  EmojiClickData,
  EmojiStyle,
  SuggestionMode,
  Categories,
} from "emoji-picker-react";
import { useThemeState } from "@/zustand/ThemeState";

interface EmojiButtonProps {
  onClick?: () => void;
  className?: string;
  insertText?: (text: string) => void;
}

const emojiPickerStyleOverride = {
  "--epr-picker-border-color": "transparent",
  "--epr-bg-color": "var(--card)",
  "--epr-category-label-bg-color": "var(--secondary)",
  "--epr-text-color": "var(--card-foreground)",
  "--epr-search-input-bg-color": "var(--input)",
  "--epr-search-input-text-color": "var(--card-foreground)",
  "--epr-search-input-border-color": "var(--border)",
  "--epr-search-input-placeholder-color": "var(--muted-foreground)",
  "--epr-category-navigation-button-color": "var(--muted-foreground)",
  "--epr-category-navigation-button-hover-color": "var(--card-foreground)",
  "--epr-emoji-hover-background": "var(--accent)",
  "--epr-picker-border-radius": "12px",
  "--epr-category-navigation-button-size": "24px",
  "--epr-emoji-size": "24px",
  "--epr-emoji-padding": "3px",
  "--epr-preview-height": "50px",
  "--epr-preview-text-size": "12px",
} as React.CSSProperties;

export function EmojiButton({
  onClick,
  className,
  insertText,
}: EmojiButtonProps) {
  const { t } = useTranslation();
  const { colorMode } = useThemeState();
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);

  const customPickerTheme = useMemo(() => {
    const isDarkMode =
      colorMode === "dark" ||
      (colorMode === "system" &&
        window.matchMedia("(prefers-color-scheme: dark)").matches);

    return {
      theme: isDarkMode ? Theme.DARK : Theme.LIGHT,
      emojiStyle: EmojiStyle.APPLE,
    };
  }, [colorMode]);

  const handleEmojiClick = (emojiData: EmojiClickData) => {
    if (insertText) {
      insertText(emojiData.emoji);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (
        pickerRef.current &&
        buttonRef.current &&
        !pickerRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setShowEmojiPicker(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  const toggleEmojiPicker = () => {
    setShowEmojiPicker((prev) => !prev);
    if (onClick) {
      onClick();
    }
  };

  return (
    <div className="relative" ref={buttonRef}>
      <SmilePlus
        size={20}
        className={cn("lg:cursor-pointer", className)}
        onClick={toggleEmojiPicker}
      />
      {showEmojiPicker && (
        <div
          ref={pickerRef}
          className={`absolute z-50 ${IS_MOBILE ? "inset-x-0 bottom-12 mx-auto max-w-[280px]" : "bottom-8 right-0"} overflow-hidden rounded-lg border border-border bg-card shadow-lg`}
        >
          <EmojiPicker
            onEmojiClick={handleEmojiClick}
            theme={customPickerTheme.theme}
            emojiStyle={customPickerTheme.emojiStyle}
            searchPlaceholder={t("emoji.search")}
            width={IS_MOBILE ? 280 : 320}
            height={IS_MOBILE ? 350 : 400}
            previewConfig={{ showPreview: false }}
            suggestedEmojisMode={SuggestionMode.RECENT}
            skinTonesDisabled={false}
            lazyLoadEmojis
            categories={[
              {
                name: t("emoji.smileysAndPeople"),
                category: Categories.SMILEYS_PEOPLE,
              },
              {
                name: t("emoji.animalsAndNature"),
                category: Categories.ANIMALS_NATURE,
              },
              {
                name: t("emoji.foodAndDrink"),
                category: Categories.FOOD_DRINK,
              },
              {
                name: t("emoji.activities"),
                category: Categories.ACTIVITIES,
              },
              {
                name: t("emoji.travelAndPlaces"),
                category: Categories.TRAVEL_PLACES,
              },
              {
                name: t("emoji.objects"),
                category: Categories.OBJECTS,
              },
              {
                name: t("emoji.symbols"),
                category: Categories.SYMBOLS,
              },
              {
                name: t("emoji.flags"),
                category: Categories.FLAGS,
              },
            ]}
            style={emojiPickerStyleOverride}
          />
        </div>
      )}
    </div>
  );
}

export default EmojiButton;
